import type { OrderStatus } from "@/types/tms";

/** Every value of the `order_status` Postgres enum. */
export const ORDER_STATUSES: OrderStatus[] = [
  "booked",
  "confirmed",
  "tender_received",
  "in_transit",
  "delivered",
  "canceled",
];

export function isOrderStatus(value: unknown): value is OrderStatus {
  return typeof value === "string" && (ORDER_STATUSES as string[]).includes(value);
}

/**
 * Coerce a form-supplied status into a valid enum value.
 *
 * A controlled Radix `Select` whose current value has no matching
 * `SelectItem` (e.g. an `in_transit` order rendered with the non-Manual
 * option list) can emit `""` through `onValueChange`. Persisting that
 * fails in Postgres with `invalid input value for enum order_status: ""`,
 * which surfaced when editing leg 2 of a multi-leg in-transit order.
 * Fall back to the row's existing status rather than writing garbage.
 */
export function safeOrderStatus(
  next: unknown,
  fallback: unknown,
): OrderStatus {
  if (isOrderStatus(next)) return next;
  if (isOrderStatus(fallback)) return fallback;
  return "booked";
}

/**
 * Status progression rank — higher means further along.
 * Canonical copy; `leg-progression.ts` and `eapi-status-mapper.ts` re-use it.
 */
export const STATUS_RANK: Record<string, number> = {
  booked: 0,
  confirmed: 1,
  tender_received: 2,
  in_transit: 3,
  delivered: 4,
  canceled: 5,
};

export interface EditStatusInput {
  /** Status value coming out of the edit form on submit. */
  formStatus: unknown;
  /** Status the dialog loaded when it opened (may be stale). */
  snapshotStatus: unknown;
  /** Status read from the database immediately before the write. */
  liveStatus: unknown;
}

/**
 * Decide which `orders.status` an edit-form save should persist.
 *
 * The edit dialog submits its whole form, including `status`, using the
 * snapshot it loaded when it opened. If something advanced the order in the
 * meantime — a driver tapping "Ready to Depart", a yard check-in, the EAPI
 * poller — that stale value silently regressed the live status
 * (last-writer-wins). See AOP-VLTK-00046: leg 1 went `in_transit`, then a
 * dispatcher save 25s later clearing `tender_id` wrote `confirmed` back over
 * it, leaving the parent stuck behind its own leg.
 *
 * Rules:
 *  - If the user explicitly changed the status field (form value differs from
 *    the snapshot), honour it — admin corrections and cancellations still work,
 *    including deliberate backward moves.
 *  - Otherwise never regress: if the live DB status ranks above the carried-over
 *    form value, keep the live status.
 */
export function resolveEditStatus(input: EditStatusInput): OrderStatus {
  const { formStatus, snapshotStatus, liveStatus } = input;
  const coerced = safeOrderStatus(formStatus, snapshotStatus);

  const userChangedStatus =
    isOrderStatus(formStatus) &&
    isOrderStatus(snapshotStatus) &&
    formStatus !== snapshotStatus;
  if (userChangedStatus) return coerced;

  if (isOrderStatus(liveStatus)) {
    const liveRank = STATUS_RANK[liveStatus] ?? -1;
    const nextRank = STATUS_RANK[coerced] ?? -1;
    if (liveRank > nextRank) return liveStatus;
  }

  return coerced;
}

